import { useCallback, useState } from 'react';
import { Content, SelectionContext, SelectionIndexes, SelectionMap, useContent } from '..';

export const SelectionProvider = ({ children }: { children: React.ReactNode }) => {
    const { content } = useContent();
    const [selected, setSelected] = useState<SelectionMap>({});
    const [selectedContent, setSelectedContent] = useState<Content[]>([]);

    const clearAll = useCallback(() => setSelected({}), []);

    const selectAll = useCallback(() => {
        if (!content) return;

        const all: SelectionMap = {};
        content.forEach(({ section, subsections }) => {
            all[section] = {};
            subsections.forEach(({ subsection, questions }) => {
                all[section][subsection] = new Set(questions.map(q => q.question));
            });
        });

        setSelected(all);
    }, [content]);

    const isSelected = useCallback(
        (section: string, subsection: string, question?: string) => {
            if (question) {
                return selected[section]?.[subsection]?.has(question) ?? false;
            }
            return selected[section]?.[subsection] !== undefined;
        },
        [selected]
    );

    const getQuestionsForSubsection = useCallback(
        (section: string, subsection: string) => {
            return (
                content
                    ?.find(s => s.section === section)
                    ?.subsections.find(s => s.subsection === subsection)
                    ?.questions.map(q => q.question) ?? []
            );
        },
        [content]
    );

    const updateSelection = useCallback((section: string, subsection: string, questions: Set<string>) => {
        setSelected(prev => {
            const next = { ...prev };
            if (!next[section]) {
                next[section] = {};
            }
            next[section][subsection] = questions;

            if (questions.size === 0) {
                delete next[section][subsection];
                if (Object.keys(next[section]).length === 0) {
                    delete next[section];
                }
            }

            return next;
        });
    }, []);

    const toggleSection = useCallback(
        (section: string, subsectionNames: string[]) => {
            const allSubsSelected = subsectionNames.every(sub => selected[section]?.[sub] !== undefined);

            if (allSubsSelected) {
                setSelected(prev => {
                    const next = { ...prev };
                    delete next[section];
                    return next;
                });
            } else {
                subsectionNames.forEach(sub => {
                    updateSelection(section, sub, new Set(getQuestionsForSubsection(section, sub)));
                });
            }
        },
        [selected, getQuestionsForSubsection, updateSelection]
    );

    const toggleSubsection = useCallback(
        (section: string, subsection: string) => {
            if (selected[section]?.[subsection]) {
                setSelected(prev => {
                    const next = { ...prev };
                    delete next[section][subsection];
                    if (Object.keys(next[section]).length === 0) {
                        delete next[section];
                    }
                    return next;
                });
            } else {
                updateSelection(section, subsection, new Set(getQuestionsForSubsection(section, subsection)));
            }
        },
        [selected, getQuestionsForSubsection, updateSelection]
    );

    const toggleQuestion = useCallback(
        (section: string, subsection: string, question: string) => {
            const currentQuestions = selected[section]?.[subsection] ?? new Set();
            const newQuestions = new Set(currentQuestions);

            if (newQuestions.has(question)) {
                newQuestions.delete(question);
            } else {
                newQuestions.add(question);
            }

            updateSelection(section, subsection, newQuestions);
        },
        [selected, updateSelection]
    );

    const getTotalItems = useCallback(() => {
        if (!content) return 0;
        return content.reduce(
            (sum, { subsections }) => sum + subsections.reduce((subSum, { questions }) => subSum + questions.length, 0),
            0
        );
    }, [content]);

    const getTotalSelected = useCallback(() => {
        return Object.values(selected).reduce(
            (sum, subsections) =>
                sum + Object.values(subsections).reduce((subSum, questions) => subSum + questions.size, 0),
            0
        );
    }, [selected]);

    const selectionsToContent = useCallback(() => {
        if (!content) return [];
        const selections: Content[] = [];
        content.forEach(({ section, subsections }) => {
            if (!selected[section]) return;
            const selectedSubsections = subsections
                .filter(s => selected[section][s.subsection])
                .map(s => ({
                    ...s,
                    questions: s.questions.filter(q => selected[section][s.subsection].has(q.question)),
                }));
            selections.push({
                section,
                blurb: content.find(c => c.section === section)?.blurb,
                subsections: selectedSubsections,
            });
        });
        setSelectedContent(selections);
        return selections;
    }, [content, selected]);

    const getSelectedIndexesFromContent = (selectedContent: Content[]): SelectionIndexes => {
        if (!content) return [];
        const indexes: SelectionIndexes = [];

        content.forEach(sectionData => {
            const selectedSection = selectedContent.find(s => s.section === sectionData.section);

            if (!selectedSection) {
                indexes.push(sectionData.subsections.map(() => []));
                return;
            }

            const subsectionIndexes = sectionData.subsections.map(sub => {
                const selectedSubsection = selectedSection.subsections.find(s => s.subsection === sub.subsection);
                if (!selectedSubsection) return [];

                return selectedSubsection.questions.map(q =>
                    sub.questions.findIndex(origQ => origQ.question === q.question)
                );
            });

            indexes.push(subsectionIndexes);
        });

        return indexes;
    };

    return (
        <SelectionContext.Provider
            value={{
                selected,
                selectedContent,
                isSelected,
                toggleSection,
                toggleSubsection,
                toggleQuestion,
                clearAll,
                selectAll,
                getTotalItems,
                getTotalSelected,
                selectionsToContent,
                getSelectedIndexesFromContent,
            }}
        >
            {children}
        </SelectionContext.Provider>
    );
};
